import React, { useState } from 'react';
import { 
  Shield, 
  Copy, 
  Check, 
  ExternalLink,
  Code,
  Image as ImageIcon,
  Palette
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const TrustSealWidget = ({ clientName = "Business" }) => {
  const [copiedCode, setCopiedCode] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState('dark');
  const [selectedSize, setSelectedSize] = useState('medium');

  const baseUrl = window.location.origin;
  const securityPageUrl = `${baseUrl}/security`;

  const sealStyles = {
    dark: {
      bg: '#1a1a1a',
      text: '#ffffff',
      accent: '#39FF14',
      border: '#333333'
    },
    light: {
      bg: '#ffffff',
      text: '#1a1a1a',
      accent: '#22c55e',
      border: '#e5e5e5'
    },
    green: {
      bg: '#052e16',
      text: '#ffffff',
      accent: '#39FF14',
      border: '#166534'
    }
  };

  const sealSizes = {
    small: { width: '140', height: '45', fontSize: '10', iconSize: '14', padding: '6' },
    medium: { width: '180', height: '55', fontSize: '12', iconSize: '18', padding: '8' },
    large: { width: '220', height: '65', fontSize: '14', iconSize: '22', padding: '10' }
  };

  const generateSvgSeal = (style, size) => {
    const colors = sealStyles[style];
    const dims = sealSizes[size];
    
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${dims.width}" height="${dims.height}" viewBox="0 0 ${dims.width} ${dims.height}">
  <defs>
    <linearGradient id="shieldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" style="stop-color:${colors.accent};stop-opacity:1" />
      <stop offset="100%" style="stop-color:${colors.accent};stop-opacity:0.7" />
    </linearGradient>
  </defs>
  <rect width="${dims.width}" height="${dims.height}" rx="6" fill="${colors.bg}" stroke="${colors.border}" stroke-width="1"/>
  <g transform="translate(${dims.padding}, ${(dims.height - dims.iconSize) / 2})">
    <path d="M${dims.iconSize/2} 1L${dims.iconSize-2} ${dims.iconSize/4}V${dims.iconSize*0.6}C${dims.iconSize-2} ${dims.iconSize*0.75} ${dims.iconSize/2} ${dims.iconSize-1} ${dims.iconSize/2} ${dims.iconSize-1}C${dims.iconSize/2} ${dims.iconSize-1} 2 ${dims.iconSize*0.75} 2 ${dims.iconSize*0.6}V${dims.iconSize/4}L${dims.iconSize/2} 1Z" fill="url(#shieldGrad)"/>
    <path d="M${dims.iconSize*0.35} ${dims.iconSize*0.5}L${dims.iconSize*0.45} ${dims.iconSize*0.6}L${dims.iconSize*0.65} ${dims.iconSize*0.4}" stroke="${colors.bg}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
  </g>
  <text x="${parseInt(dims.padding) + parseInt(dims.iconSize) + 8}" y="${dims.height/2 - 4}" font-family="system-ui, -apple-system, sans-serif" font-size="${dims.fontSize}" font-weight="600" fill="${colors.text}">Secured by</text>
  <text x="${parseInt(dims.padding) + parseInt(dims.iconSize) + 8}" y="${dims.height/2 + parseInt(dims.fontSize) - 2}" font-family="system-ui, -apple-system, sans-serif" font-size="${dims.fontSize}" font-weight="700" fill="${colors.accent}">My Biz Consulting</text>
</svg>`;
  };

  const generateHtmlEmbed = (style, size) => {
    const svg = generateSvgSeal(style, size);
    const encodedSvg = encodeURIComponent(svg);
    
    return `<!-- My Biz Consulting Trust Seal -->
<a href="${securityPageUrl}" target="_blank" rel="noopener noreferrer" title="Secured by My Biz Consulting - Enterprise-Grade Security" style="display:inline-block;text-decoration:none;">
  <img src="data:image/svg+xml,${encodedSvg}" alt="Secured by My Biz Consulting" />
</a>`;
  };

  const generateReactEmbed = (style, size) => {
    const colors = sealStyles[style];
    const dims = sealSizes[size];
    
    return `// My Biz Consulting Trust Seal Component
<a 
  href="${securityPageUrl}" 
  target="_blank" 
  rel="noopener noreferrer"
  style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '${dims.padding}px ${parseInt(dims.padding) + 4}px',
    backgroundColor: '${colors.bg}',
    border: '1px solid ${colors.border}',
    borderRadius: '6px',
    textDecoration: 'none',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  }}
>
  <svg width="${dims.iconSize}" height="${dims.iconSize}" viewBox="0 0 24 24" fill="${colors.accent}">
    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
  </svg>
  <div>
    <div style={{ fontSize: '${dims.fontSize}px', color: '${colors.text}', fontWeight: 600 }}>Secured by</div>
    <div style={{ fontSize: '${dims.fontSize}px', color: '${colors.accent}', fontWeight: 700 }}>My Biz Consulting</div>
  </div>
</a>`;
  };

  const copyToClipboard = async (code, type) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(type);
      toast.success('Code copied to clipboard!');
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      toast.error('Failed to copy code');
    }
  };

  const PreviewSeal = ({ style, size }) => {
    const colors = sealStyles[style];
    const dims = sealSizes[size];
    
    return (
      <a 
        href={securityPageUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 no-underline transition-transform hover:scale-105"
        style={{
          padding: `${dims.padding}px ${parseInt(dims.padding) + 4}px`,
          backgroundColor: colors.bg,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
        }}
      >
        <Shield 
          style={{ 
            width: dims.iconSize, 
            height: dims.iconSize, 
            color: colors.accent 
          }} 
        />
        <div>
          <div style={{ 
            fontSize: `${dims.fontSize}px`, 
            color: colors.text, 
            fontWeight: 600,
            lineHeight: 1.2
          }}>
            Secured by
          </div>
          <div style={{ 
            fontSize: `${dims.fontSize}px`, 
            color: colors.accent, 
            fontWeight: 700,
            lineHeight: 1.2
          }}>
            My Biz Consulting
          </div>
        </div>
      </a>
    );
  };

  return (
    <Card className="border-green-200 bg-gradient-to-br from-green-50/50 to-white">
      <CardHeader>
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <Shield className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <CardTitle className="text-lg">Trust Seal Widget</CardTitle>
            <CardDescription>
              Add this badge to your website to show clients you use enterprise-grade security
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Style & Size Selection */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-neutral-700 mb-2 block">Style</label>
            <div className="flex gap-2">
              {Object.keys(sealStyles).map((style) => (
                <button
                  key={style}
                  onClick={() => setSelectedStyle(style)}
                  className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                    selectedStyle === style
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-neutral-200 hover:border-neutral-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-4 h-4 rounded-full border"
                      style={{ backgroundColor: sealStyles[style].bg, borderColor: sealStyles[style].border }}
                    />
                    {style.charAt(0).toUpperCase() + style.slice(1)}
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-neutral-700 mb-2 block">Size</label>
            <div className="flex gap-2">
              {Object.keys(sealSizes).map((size) => (
                <button
                  key={size}
                  onClick={() => setSelectedSize(size)}
                  className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                    selectedSize === size
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-neutral-200 hover:border-neutral-300'
                  }`}
                >
                  {size.charAt(0).toUpperCase() + size.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="p-6 bg-neutral-100 rounded-xl flex items-center justify-center">
          <div className="text-center">
            <p className="text-xs text-neutral-500 mb-3">Preview (click to test link)</p>
            <PreviewSeal style={selectedStyle} size={selectedSize} />
          </div>
        </div>

        {/* Embed Code Tabs */}
        <Tabs defaultValue="html" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="html" className="flex items-center gap-2">
              <Code className="w-4 h-4" />
              HTML Embed
            </TabsTrigger>
            <TabsTrigger value="react" className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4" />
              React/JSX
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="html" className="mt-4">
            <div className="relative">
              <pre className="bg-neutral-900 text-neutral-100 p-4 rounded-lg overflow-x-auto text-xs">
                <code>{generateHtmlEmbed(selectedStyle, selectedSize)}</code>
              </pre>
              <Button
                size="sm"
                variant="secondary"
                className="absolute top-2 right-2"
                onClick={() => copyToClipboard(generateHtmlEmbed(selectedStyle, selectedSize), 'html')}
              >
                {copiedCode === 'html' ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-neutral-500 mt-2">
              Paste this code anywhere in your website's HTML where you want the badge to appear.
            </p>
          </TabsContent>
          
          <TabsContent value="react" className="mt-4">
            <div className="relative">
              <pre className="bg-neutral-900 text-neutral-100 p-4 rounded-lg overflow-x-auto text-xs">
                <code>{generateReactEmbed(selectedStyle, selectedSize)}</code>
              </pre>
              <Button
                size="sm"
                variant="secondary"
                className="absolute top-2 right-2"
                onClick={() => copyToClipboard(generateReactEmbed(selectedStyle, selectedSize), 'react')}
              >
                {copiedCode === 'react' ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-neutral-500 mt-2">
              Use this component in your React/Next.js application.
            </p>
          </TabsContent>
        </Tabs>

        {/* Benefits */}
        <div className="bg-green-50 rounded-lg p-4 border border-green-100">
          <h4 className="font-medium text-green-800 mb-2 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Why add a Trust Seal?
          </h4>
          <ul className="text-sm text-green-700 space-y-1">
            <li>• Builds trust with your website visitors</li>
            <li>• Shows you take security seriously</li>
            <li>• Links to detailed security practices</li>
            <li>• Professional, modern design</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default TrustSealWidget;
